import {useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import DotsLoading from '../../../../components/dotsLoading';
import HeaderBasketButton from '../../../../components/headerBasketButton';
import CustomSearchBox from '../../../../components/mazraeKit/customSearchBox';
import ShareTitleHeader from '../../../../components/mazraeKit/headers/shareTitleHeader';
import NoItemFoundPage from '../../../../components/noItemFoundPage';
import SpinnerLoading from '../../../../components/SpinnerLoading';
import {getInputGroupByCode} from '../../../../module/dashboard/category/utilities';
import useGetAdLikeStatus from '../../../../services/hookService/getAdLikeStatus/useGetAdLikeStatus';
import {IInputShopAdvertisements} from '../../../../services/hookService/getAvailableStoreAdsByInput/types';
import UseGetShopAdvertisementsByInput from '../../../../services/hookService/getAvailableStoreAdsByInput/UseGetShopAdvertisementsByInput';
import useGetDetailShopCard from '../../../../services/hookService/getDetailShopCard/useGetDetailShopCard';
import usePatchPostLike from '../../../../services/hookService/patchPostLike/usePatchPostLike';
import useDebounce from '../../../../utilities/hooks/useDebounce/useDebounce';
import ProductShopItemCard from '../../products/ProductShopItemCard';
import AdvertisementFilterBottomSheet from './AdvertisementFilterBottomSheet';

const SelectShopAdvertisementByInput = () => {
  const {selectedInputGroup, storeGuid, inputGuid} = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const search = searchParams.get('search') || '';
  const filterAdType = searchParams.get('filterType')?.split(',') || [];

  const [showFiltersBottomSheet, setShowFiltersBottomSheet] = useState(false);
  const debouncedSearch = useDebounce(search, 1000);

  const {cardDetailData, isLoading: isShopDetailLoading} = useGetDetailShopCard(
    {guid: storeGuid!},
  );

  const {
    data,
    isLoading: isShopAdvertisementListLoading,
    loaderRef,
    isFetchingNextPage,
  } = UseGetShopAdvertisementsByInput({
    storeGuid,
    input_group: +selectedInputGroup!,
    input_guid: inputGuid,
    search: debouncedSearch,
    available: filterAdType.includes('available') ? true : undefined,
    has_price: filterAdType.includes('has_price') ? true : undefined,
  });

  const searchTitle = selectedInputGroup
    ? getInputGroupByCode[Number(selectedInputGroup)]
    : 'آگهی ها';

  const isAvailable = (item: IInputShopAdvertisements): boolean => {
    const actualAvailable = item.available_quantity - item.hold_quantity;
    return (
      item.available_quantity > 0 && actualAvailable >= item.min_sell_quantity
    );
  };

  const {editLikePost} = usePatchPostLike({
    onSuccessAction: () => {
      refetch();
    },
  });

  const {adLikeStatusData, refetch} = useGetAdLikeStatus(
    storeGuid || '',
    !!storeGuid,
    'stores',
  );

  const updateUrlParams = (newSearch: string, newFilterAdType: string[]) => {
    const params = new URLSearchParams();

    if (newSearch) {
      params.set('search', newSearch);
    }

    if (newFilterAdType.length > 0) {
      params.set('filterType', newFilterAdType.join(','));
    }

    const newUrl = params.toString()
      ? `${location.pathname}?${params.toString()}`
      : location.pathname;

    navigate(newUrl, {replace: true});
  };

  const handleSearchChange = (value: string) => {
    updateUrlParams(value, filterAdType);
  };

  const handleFilterChange = (newFilterAdType: string[]) => {
    updateUrlParams(search, newFilterAdType);
  };

  const isLoading = isShopAdvertisementListLoading || isShopDetailLoading;

  return (
    <div className="flex flex-col w-full min-h-screen bg-white">
      {/* Sticky Header Section */}
      <div className="sticky top-0 z-30 bg-white">
        <ShareTitleHeader
          title={cardDetailData?.title || ''}
          isLiked={adLikeStatusData?.like_status || false}
          onClickLike={() =>
            cardDetailData?.guid &&
            editLikePost({id: cardDetailData.guid, liking_type: 'stores'})
          }
          isSaleAgent={cardDetailData?.is_sale_agent ?? false}
          rightExtra={<HeaderBasketButton />}
        />
      </div>

      {/* Sticky Search and Filter Bar - below header */}
      <div className="sticky top-[56px] z-20 bg-white border-b border-gray-100">
        <div className="px-6 py-3">
          <div className="flex items-center justify-center gap-3">
            <CustomSearchBox
              placeholder={`جستجو در ${searchTitle}`}
              inputValue={search}
              setInputValue={handleSearchChange}
              boxStyles="sm"
              inputCustomClass="!pr-2 border-gray-300"
              removeIcon="icon-nameClose-remove"
              preIcon="icon-nameSearch"
              onChange={e => handleSearchChange(e.target.value)}
            />
            <div className="w-[30%]">
              <button
                className={`flex items-center justify-center gap-1 px-4 py-2 border-[1px] rounded-full w-full transition-all duration-200 ${
                  Boolean(filterAdType?.length > 0)
                    ? 'bg-farm-shop-50/90 text-farm-shop-500 border-farm-shop-500'
                    : 'text-gray-700 border-gray-300 hover:border-gray-400'
                }`}
                onClick={() => setShowFiltersBottomSheet(true)}
              >
                <i className="text-xl icon-nameFilter" />
                <span className="text-sm whitespace-nowrap">فیلتر کردن</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1">
        <div className="px-6 pb-6">
          {(data?.length as number) > 0 && !isLoading ? (
            <div className="grid grid-cols-2 gap-3">
              {data?.map(item => (
                <div key={item?.id}>
                  <ProductShopItemCard
                    item={item!}
                    isAvailable={isAvailable(item!)}
                    saleMethod={cardDetailData?.sale_method!}
                  />
                </div>
              ))}
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <SpinnerLoading size={45} />
            </div>
          ) : (
            <div className="min-h-[400px]">
              <NoItemFoundPage />
            </div>
          )}

          {/* This div acts as a sentinel for triggering infinite scroll */}
          <div ref={loaderRef} className="mt-4 text-center">
            {isFetchingNextPage && <DotsLoading />}
          </div>
        </div>
      </div>

      <AdvertisementFilterBottomSheet
        isOpen={showFiltersBottomSheet}
        setIsOpen={setShowFiltersBottomSheet}
        filterAdType={filterAdType}
        setFilterAdType={handleFilterChange}
      />
    </div>
  );
};

export default SelectShopAdvertisementByInput;
